import React, { useMemo, useState } from 'react';
import { MuiBox, MuiTable, SvgIcon, SvgIconButton, Card, Box, Tooltip, Switch } from '@kis-technologies/kis-ui';
import { useStyles } from '../styled';
import { PriceType } from '../types';
import { TicketTemplate } from 'store/ticketTemplates';

interface PriceTypesTableProps {
  priceTypes: PriceType[];
  templates: TicketTemplate[];
  onEdit: (priceType: PriceType) => void;
  onCopy: (priceType: PriceType) => void;
  onDelete: (priceType: PriceType) => void;
  onToggle: (priceType: PriceType) => void;
  permissions: {
    edit: boolean;
    copy: boolean;
    delete: boolean;
    toggle: boolean;
  };
}

const PriceTypesTable: React.FC<PriceTypesTableProps> = ({
  priceTypes,
  templates,
  onEdit,
  onCopy,
  onDelete,
  onToggle,
  permissions
}) => {
  const canCopy = permissions.copy;
  const canDelete = permissions.delete;
  const canToggle = permissions.toggle;
  const classes = useStyles();
  const [sortParams, setSortParams] = useState<{ column: string; order: 'asc' | 'desc' }>({
    column: 'name',
    order: 'asc'
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sort = (data: any[], column: string, order: string) => {
    return [...data].sort((a, b) => {
      let valA = a[column] ?? '';
      let valB = b[column] ?? '';

      if (typeof valA === 'string' && typeof valB === 'string') {
        valA = valA.toLowerCase();
        valB = valB.toLowerCase();
      }

      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getTemplateName = (templateId: string | null) => {
    if (!templateId) return '--';
    const template = templates.find((t) => t.id === templateId);
    return template ? template.name : '--';
  };

  const mappedPriceTypes = priceTypes.map((pt) => ({
    ...pt,
    templateName: getTemplateName(pt.ticketTemplateId)
  }));

  const sortedPriceTypes = useMemo(() => {
    return sort(mappedPriceTypes, sortParams.column, sortParams.order);
  }, [mappedPriceTypes, sortParams]);

  const handleHeaderClick = (column: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setSortParams((prev: any) => ({
      column,
      order: prev.column === column && prev.order === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortArrow = () => (
    <SvgIcon name={sortParams.order === 'asc' ? 'drop_up' : 'drop_down'} size={15} style={{ marginLeft: 5 }} />
  );

  return (
    <MuiBox className={classes.outerBox}>
      <Card hasBorderRadius raised fullHeight>
        <Box flexOverride="1">
          <MuiTable.TableContainer className={classes.tableContainer}>
            <MuiTable.Table stickyHeader>
              <MuiTable.TableHead>
                <MuiTable.TableRow>
                  <MuiTable.TableCell
                    onClick={() => handleHeaderClick('name')}
                    className={classes.tableHeaders}
                    sx={{ width: '200px' }}
                  >
                    Name {sortParams.column === 'name' && <SortArrow />}
                  </MuiTable.TableCell>
                  <MuiTable.TableCell
                    onClick={() => handleHeaderClick('templateName')}
                    className={classes.tableHeaders}
                    sx={{ width: '200px' }}
                  >
                    Template {sortParams.column === 'templateName' && <SortArrow />}
                  </MuiTable.TableCell>
                  <MuiTable.TableCell
                    className={classes.tableHeaders}
                    onClick={() => handleHeaderClick('price')}
                    align="center"
                    sx={{ minWidth: '10ch', whiteSpace: 'nowrap' }}
                  >
                    Price {sortParams.column === 'price' && <SortArrow />}
                  </MuiTable.TableCell>
                  <MuiTable.TableCell className={classes.tableHeaders} align="center" sx={{ width: '11%' }}>
                    Enabled
                  </MuiTable.TableCell>
                  <MuiTable.TableCell className={classes.tableHeaders} align="center" sx={{ width: '13%' }}>
                    Actions
                  </MuiTable.TableCell>
                </MuiTable.TableRow>
              </MuiTable.TableHead>

              <MuiTable.TableBody>
                {sortedPriceTypes.map((pt) => {
                  return (
                    <MuiTable.TableRow key={pt.id}>
                      <MuiTable.TableCell>{pt.name}</MuiTable.TableCell>
                      <MuiTable.TableCell> {getTemplateName(pt.ticketTemplateId)}</MuiTable.TableCell>
                      <MuiTable.TableCell align="center" sx={{ minWidth: '10ch', whiteSpace: 'nowrap' }}>
                        {pt.price ? `$ ${Number(pt.price).toFixed(2)}` : '--'}
                      </MuiTable.TableCell>
                      <MuiTable.TableCell align="center">
                        <MuiBox display="flex" justifyContent="center">
                          <Switch
                            checked={pt.enabled}
                            onChange={() => onToggle(pt)}
                            disabled={!canToggle}
                            labelSx={{ display: 'none' }}
                          />
                        </MuiBox>
                      </MuiTable.TableCell>
                      <MuiTable.TableCell align="right">
                        <MuiBox display="flex" gap={1} justifyContent="center">
                          <Tooltip title="Edit Price Type">
                            <SvgIconButton onClick={() => onEdit(pt)}>
                              <SvgIcon name="edit_circle" />
                            </SvgIconButton>
                          </Tooltip>
                          {canCopy && (
                            <Tooltip title="Copy Price Type">
                              <SvgIconButton onClick={() => onCopy(pt)}>
                                <SvgIcon name="copy_circle" />
                              </SvgIconButton>
                            </Tooltip>
                          )}
                          {canDelete && (
                            <Tooltip title="Delete Price Type">
                              <SvgIconButton onClick={() => onDelete(pt)}>
                                <SvgIcon name="exit_circle" />
                              </SvgIconButton>
                            </Tooltip>
                          )}
                        </MuiBox>
                      </MuiTable.TableCell>
                    </MuiTable.TableRow>
                  );
                })}
              </MuiTable.TableBody>
            </MuiTable.Table>
          </MuiTable.TableContainer>
        </Box>
      </Card>
    </MuiBox>
  );
};

export default PriceTypesTable;
